import React, { useState, useEffect } from 'react';
import { X, FileEdit, CheckCircle, Loader2 } from 'lucide-react';
import { useTranslation } from '../../context/LanguageContext';
import { useNotification } from '../../context/NotificationContext';
import { usePermission } from '../../context/PermissionContext';

/**
 * Edit Status Modal
 *
 * 对渠道产品执行编辑状态确认，更新 channel_product_edit_status 表的 status 字段。
 * 支持单条和批量模式。
 *
 * Props:
 *   channelType   - 'magento2'
 *   entityId      - (单条) entity ID
 *   entityIds     - (批量) entity ID 数组
 *   sku           - 产品 SKU（单条时显示）
 *   currentStatus - 当前编辑状态（单条时显示）
 *   onClose       - () => void
 *   onSuccess     - () => void
 */
const ChannelProductEditStatusModal = ({ channelType, entityId, entityIds, sku, currentStatus, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const { hasPermission } = usePermission();

  const isBatch = Array.isArray(entityIds) && entityIds.length > 0;

  const [loading, setLoading] = useState(!isBatch);
  const [submitting, setSubmitting] = useState(false);
  const [statusRecord, setStatusRecord] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(currentStatus || 'draft');

  // Load existing edit status record（单条模式）
  useEffect(() => {
    if (!entityId || isBatch) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await fetch(`/api/channel/eav/products/edit-status-check?entity_id=${entityId}`);
        const data = await res.json();
        if (data.status === 'success' && data.data) {
          setStatusRecord(data.data);
          setSelectedStatus(data.data.status || 'draft');
        } else {
          setStatusRecord(null);
          setSelectedStatus(currentStatus || 'draft');
        }
      } catch (e) {
        setStatusRecord(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [entityId, currentStatus, isBatch]);

  const handleSubmit = async () => {
    if (!hasPermission('channel_products', 'edit_status')) return;
    setSubmitting(true);
    try {
      const url = isBatch
        ? '/api/channel/eav/products/edit-status/batch-update'
        : '/api/channel/eav/products/edit-status/update';
      const body = isBatch
        ? { entity_ids: entityIds, status: selectedStatus }
        : { entity_id: entityId, status: selectedStatus };
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.status === 'success') {
        notify(t('common.success'), 'success');
        onSuccess && onSuccess();
      } else {
        notify(data.message || t('common.error'), 'error');
      }
    } catch (e) {
      notify(t('common.error'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const statusOptions = [
    { value: 'draft', label: t('edit_status_draft') || 'Draft', icon: FileEdit, color: 'text-amber-600 border-amber-300 hover:bg-amber-50 dark:border-amber-600 dark:hover:bg-amber-900/20' },
    { value: 'processing', label: t('edit_status_processing') || 'Processing', icon: Loader2, color: 'text-blue-600 border-blue-300 hover:bg-blue-50 dark:border-blue-600 dark:hover:bg-blue-900/20' },
    { value: 'edited', label: t('edit_status_edited') || 'Edited', icon: CheckCircle, color: 'text-green-600 border-green-300 hover:bg-green-50 dark:border-green-600 dark:hover:bg-green-900/20' },
  ];

  return (
    <div className="fixed inset-0 bg-black/40 dark:bg-black/60 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden"
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
            {t('edit_status') || 'Edit Status'}
          </h2>
          <button onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {/* Product info */}
          <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500 dark:text-slate-400">{t('channel.sku')}</span>
              <span className="font-mono text-slate-800 dark:text-slate-200">{sku || '-'}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500 dark:text-slate-400">{t('channel.entity_id')}</span>
              <span className="font-mono text-slate-800 dark:text-slate-200">{entityId || '-'}</span>
            </div>
            {statusRecord?.edited_by_name && (
              <div className="flex justify-between text-sm">
                <span className="text-slate-500 dark:text-slate-400">{t('edit_status_edited_by') || 'Edited By'}</span>
                <span className="text-slate-800 dark:text-slate-200">{statusRecord.edited_by_name}</span>
              </div>
            )}
            {statusRecord?.edited_at && (
              <div className="flex justify-between text-sm">
                <span className="text-slate-500 dark:text-slate-400">{t('edit_status_edited_at') || 'Edited At'}</span>
                <span className="text-slate-800 dark:text-slate-200">{statusRecord.edited_at}</span>
              </div>
            )}
          </div>

          {/* Status selector */}
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 size={24} className="animate-spin text-slate-400" />
            </div>
          ) : (
            <div className="space-y-3">
              <label className="block text-sm font-medium text-slate-600 dark:text-slate-400">
                {t('edit_status_select') || 'Select Status'}
              </label>
              <div className="grid grid-cols-3 gap-3">
                {statusOptions.map(opt => {
                  const Icon = opt.icon;
                  const isSelected = selectedStatus === opt.value;
                  return (
                    <button key={opt.value}
                      onClick={() => setSelectedStatus(opt.value)}
                      className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                        isSelected
                          ? `${opt.color} bg-white dark:bg-slate-700 shadow-sm`
                          : 'border-slate-200 dark:border-slate-600 text-slate-500 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-500'
                      }`}>
                      <Icon size={24} />
                      <span className="text-xs font-medium">{opt.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700">
          <button onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
            {t('common.cancel')}
          </button>
          <button onClick={handleSubmit} disabled={submitting || loading || !hasPermission('channel_products', 'edit_status')}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors inline-flex items-center gap-2">
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChannelProductEditStatusModal;
